import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Languages, Loader2, CheckCircle2, AlertCircle, Zap } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/hooks/useLanguage';

interface Product {
  id: string;
  title: string;
  description: string | null;
  title_translations: Record<string, string>;
  description_translations: Record<string, string>;
}

interface TranslationProgress {
  total: number;
  completed: number;
  failed: number;
  current: string;
}

export const BulkProductTranslation = () => {
  const [isTranslating, setIsTranslating] = useState(false);
  const [progress, setProgress] = useState<TranslationProgress>({
    total: 0,
    completed: 0,
    failed: 0,
    current: ''
  });
  const [untranslatedCount, setUntranslatedCount] = useState<number | null>(null);
  const { toast } = useToast();
  const { t } = useLanguage();

  const checkUntranslatedProducts = async () => {
    try {
      const { data: products, error } = await supabase
        .from('products')
        .select('id, title, description, title_translations, description_translations')
        .eq('active', true);

      if (error) throw error;

      const untranslated = products?.filter(p => {
        const translations = p.title_translations || {};
        // Vérifie si au moins une langue manque
        return !translations.en || !translations['lg-fr'] || !translations.sw;
      });

      setUntranslatedCount(untranslated?.length || 0);
      return untranslated?.length || 0;
    } catch (error) {
      console.error('Error checking products:', error);
      toast({
        title: "Erreur",
        description: "Impossible de vérifier les produits",
        variant: "destructive"
      });
      return 0;
    }
  };

  const translateSingleProduct = async (product: Product): Promise<boolean> => {
    try {
      const languages = ['fr', 'en', 'lg-fr', 'sw'] as const;
      const titleTranslations: Record<string, string> = { ...product.title_translations };
      const descriptionTranslations: Record<string, string> = { ...product.description_translations };

      // Traduire dans chaque langue manquante
      for (const lang of languages) {
        if (!titleTranslations[lang] || titleTranslations[lang].trim() === '') {
          try {
            const { data, error } = await supabase.functions.invoke('translate-product', {
              body: {
                productId: product.id,
                title: product.title,
                description: product.description || '',
                targetLanguage: lang,
              },
            });

            if (!error && data) {
              titleTranslations[lang] = data.title || product.title;
              descriptionTranslations[lang] = data.description || product.description || '';
            } else {
              // Fallback : copier le titre original
              titleTranslations[lang] = product.title;
              descriptionTranslations[lang] = product.description || '';
            }
          } catch (err) {
            console.warn(`Translation failed for ${lang}, using fallback`, err);
            titleTranslations[lang] = product.title;
            descriptionTranslations[lang] = product.description || '';
          }

          // Petit délai pour ne pas surcharger l'API
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      // Mettre à jour le produit
      const { error: updateError } = await supabase
        .from('products')
        .update({
          title_translations: titleTranslations,
          description_translations: descriptionTranslations
        })
        .eq('id', product.id);

      if (updateError) throw updateError;

      return true;
    } catch (error) {
      console.error(`Error translating product ${product.id}:`, error);
      return false;
    }
  };

  const startBulkTranslation = async () => {
    setIsTranslating(true);
    
    try {
      // Récupérer tous les produits non traduits
      const { data: products, error } = await supabase
        .from('products')
        .select('id, title, description, title_translations, description_translations')
        .eq('active', true);

      if (error) throw error;

      const untranslated = products?.filter(p => {
        const translations = p.title_translations || {};
        return !translations.en || !translations['lg-fr'] || !translations.sw;
      }) || [];

      if (untranslated.length === 0) {
        toast({
          title: "Aucun produit à traduire",
          description: "Tous les produits sont déjà traduits",
        });
        setIsTranslating(false);
        return;
      }

      setProgress({
        total: untranslated.length,
        completed: 0,
        failed: 0,
        current: ''
      });

      let completed = 0;
      let failed = 0;

      // Traduire chaque produit
      for (const product of untranslated) {
        setProgress(prev => ({
          ...prev,
          current: product.title
        }));

        const success = await translateSingleProduct(product);
        
        if (success) {
          completed++;
        } else {
          failed++;
        }

        setProgress(prev => ({
          ...prev,
          completed: completed,
          failed: failed
        }));
      }

      // Message final
      toast({
        title: "Traduction terminée !",
        description: `${completed} produits traduits avec succès${failed > 0 ? `, ${failed} échecs` : ''}`,
      });

      // Rafraîchir le compteur
      await checkUntranslatedProducts();

    } catch (error) {
      console.error('Bulk translation error:', error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de la traduction",
        variant: "destructive"
      });
    } finally {
      setIsTranslating(false);
    }
  };

  // Vérifier au chargement
  useState(() => {
    checkUntranslatedProducts();
  });

  const progressPercentage = progress.total > 0 
    ? Math.round((progress.completed / progress.total) * 100)
    : 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Languages className="h-5 w-5" />
            <CardTitle>{t('admin.bulkTranslation.title') || 'Traduction en masse des produits'}</CardTitle>
          </div>
          {untranslatedCount !== null && untranslatedCount > 0 && (
            <Badge variant="destructive" className="ml-2">
              {untranslatedCount} {t('admin.bulkTranslation.untranslated') || 'non traduits'}
            </Badge>
          )}
        </div>
        <CardDescription>
          {t('admin.bulkTranslation.description') || 'Traduisez automatiquement tous les produits non traduits dans les 4 langues'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!isTranslating && untranslatedCount !== null && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {untranslatedCount === 0 
                ? (t('admin.bulkTranslation.allTranslated') || 'Tous les produits sont traduits !')
                : `${untranslatedCount} ${t('admin.bulkTranslation.productsNeedTranslation') || 'produits nécessitent une traduction'}`
              }
            </AlertDescription>
          </Alert>
        )}

        {isTranslating && (
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">
                  {t('admin.bulkTranslation.progress') || 'Progression'}: {progress.completed} / {progress.total}
                </span>
                <span className="text-muted-foreground">{progressPercentage}%</span>
              </div>
              <Progress value={progressPercentage} className="h-2" />
            </div>

            {progress.current && (
              <Alert>
                <Loader2 className="h-4 w-4 animate-spin" />
                <AlertDescription>
                  {t('admin.bulkTranslation.translating') || 'Traduction en cours'}: <strong>{progress.current}</strong>
                </AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                <span>{t('admin.bulkTranslation.completed') || 'Réussis'}: {progress.completed}</span>
              </div>
              <div className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-red-500" />
                <span>{t('admin.bulkTranslation.failed') || 'Échoués'}: {progress.failed}</span>
              </div>
            </div>
          </div>
        )}

        <div className="flex gap-2">
          <Button
            onClick={startBulkTranslation}
            disabled={isTranslating || untranslatedCount === 0}
            className="gap-2"
          >
            {isTranslating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('admin.bulkTranslation.translatingButton') || 'Traduction en cours...'}
              </>
            ) : (
              <>
                <Zap className="h-4 w-4" />
                {t('admin.bulkTranslation.startButton') || 'Traduire tous les produits'}
              </>
            )}
          </Button>

          <Button
            onClick={checkUntranslatedProducts}
            variant="outline"
            disabled={isTranslating}
          >
            {t('admin.bulkTranslation.refreshButton') || 'Actualiser'}
          </Button>
        </div>

        <Alert variant="default" className="bg-blue-50 border-blue-200">
          <AlertDescription className="text-sm text-blue-800">
            <strong>{t('admin.bulkTranslation.note') || 'Note'}:</strong>{' '}
            {t('admin.bulkTranslation.noteText') || 'La traduction peut prendre plusieurs minutes selon le nombre de produits. Chaque produit sera traduit dans les 3 langues manquantes (EN, LG, SW).'}
          </AlertDescription>
        </Alert>
      </CardContent>
    </Card>
  );
};
